import { apiClient } from '@/config/api';
import { Alarm, AlarmType, HydraCollection, PaginatedResponse } from '@/types';

export interface CreateAlarmData {
  title: string;
  currentValue?: string;
  normaleValue: string;
  operator?: string;
  code?: string;
  status?: 'OPEN' | 'CLOSED';
  device?: string; // IRI: /api/devices/{id}
  attribute?: string; // IRI: /api/attributes/{id}
  box?: string; // IRI: /api/boxes/{id}
  type: string; // IRI: /api/alarm_types/{id}
}

export interface UpdateAlarmData {
  title?: string;
  currentValue?: string;
  normaleValue?: string;
  operator?: string;
  code?: string;
  status?: 'OPEN' | 'CLOSED';
  device?: string;
  attribute?: string;
  box?: string;
  type?: string;
}

export interface GetAlarmsParams {
  page?: number;
  itemsPerPage?: number;
  status?: 'OPEN' | 'CLOSED';
  projectId?: number;
}

export const alarmService = {
  // Get all alarms with pagination and filters
  async getAlarms(params?: GetAlarmsParams): Promise<PaginatedResponse<Alarm>> {
    const page = params?.page || 1;
    const itemsPerPage = params?.itemsPerPage || 20;
    const status = params?.status;
    const projectId = params?.projectId;

    const requestParams: Record<string, unknown> = {
      // Legacy alarms endpoint (/alarms) has been removed; use alarm events.
      'groups[]': ['alarmEvent:read'],
      page: page,
      itemsPerPage: itemsPerPage,
    };

    if (status) {
      requestParams['status'] = status;
    }

    if (projectId) {
      // Filter by project through device/box/attribute
      // This might need custom API endpoint
      requestParams['project'] = `/api/projects/${projectId}`;
    }

    // Use alarm events collection (read-only)
    const response = await apiClient.get<HydraCollection<any>>('/alarm_events', {
      params: requestParams,
    });

    const data = response.data;
    const events = data['hydra:member'] || data['member'] || [];
    const totalItems = data['hydra:totalItems'] || data['totalItems'] || 0;
    const view = data['hydra:view'] || data['view'];

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const hasNextPage = !!view?.['hydra:next'];
    const hasPreviousPage = !!view?.['hydra:previous'];

    // Map AlarmEvent -> legacy Alarm shape (best-effort) so the existing UI keeps working.
    const alarms: Alarm[] = events.map((e: any) => ({
      id: typeof e.id === 'string' ? parseInt(e.id, 10) : e.id,
      title: e.title ?? '',
      currentValue: e.currentValueRaw ?? e.currentValueNum ?? undefined,
      normaleValue: e.thresholdValueRaw ?? e.thresholdValueNum ?? '',
      operator: e.operator ?? undefined,
      code: e.code ?? undefined,
      status: e.status === 'CLOSED' ? 'CLOSED' : 'OPEN',
      createdAt: e.openedAt ?? e.createdAt ?? new Date().toISOString(),
      device: typeof e.device === 'object' ? e.device : undefined,
      attribute: typeof e.attribute === 'object' ? e.attribute : undefined,
      box: typeof e.box === 'object' ? e.box : undefined,
      type: typeof e.type === 'object' ? e.type : ({} as any),
    }));

    return {
      data: alarms,
      totalItems,
      currentPage: page,
      itemsPerPage,
      totalPages,
      hasNextPage,
      hasPreviousPage,
    };
  },

  // Get single alarm
  async getAlarm(id: number): Promise<Alarm> {
    const response = await apiClient.get<Alarm>(`/alarms/${id}`, {
      params: {
        'groups[]': ['alarm:read'],
      },
    });
    return response.data;
  },

  // Create alarm
  async createAlarm(data: CreateAlarmData): Promise<Alarm> {
    const response = await apiClient.post('/alarms', {
      ...data,
      'groups[]': ['alarm:write'],
    });
    return response.data;
  },

  // Update alarm
  async updateAlarm(id: number, data: UpdateAlarmData): Promise<Alarm> {
    const response = await apiClient.put(`/alarms/${id}`, {
      ...data,
      'groups[]': ['alarm:write'],
    });
    return response.data;
  },

  // Delete alarm
  async deleteAlarm(id: number): Promise<void> {
    await apiClient.delete(`/alarms/${id}`);
  },

  // Get alarm types
  async getAlarmTypes(): Promise<AlarmType[]> {
    const response = await apiClient.get<HydraCollection<AlarmType>>('/alarm_types', {
      params: {
        'groups[]': ['alarmType:read'],
      },
    });
    const data = response.data;
    return data['hydra:member'] || data['member'] || [];
  },
};
